import { Link } from "react-router-dom";
import { Shield, Zap, Smartphone, MessageCircle, ShieldCheck, ArrowRight, Check, LayoutDashboard } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import PublicFooter from "../components/PublicFooter";

const TIERS = [
  {
    code: "merchant_lite", name: "Merchant Lite", premium: 99, cover: 50000,
    perks: ["M-Pesa fraud cover", "Phishing & ATO loss", "WhatsApp claim filing"],
    flash: false,
  },
  {
    code: "social_vendor_pro", name: "Social Vendor Pro", premium: 250, cover: 250000,
    perks: ["Everything in Lite", "WhatsApp Business compromise", "Priority FlashClaim queue"],
    flash: false,
  },
  {
    code: "enterprise_sme", name: "Enterprise SME", premium: 600, cover: 1000000,
    perks: ["All Pro benefits", "POS settlement fraud", "Dedicated SIU concierge"],
    flash: true,
  },
];

export default function LandingPage() {
  useTranslation(); // ensure i18n initialised
  const { user } = useAuth();
  const dashHref = user
    ? (user.role === "merchant" ? "/merchant"
      : user.role === "partner_admin" ? "/partner" : "/app")
    : null;
  return (
    <div className="min-h-screen bg-[#f4f2eb] text-[#1b1d1c]" data-testid="landing-page">
      {/* Nav */}
      <nav className="px-6 lg:px-10 py-5 flex items-center justify-between max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
          <div className="relative">
            <Shield className="w-7 h-7 text-[#114232]" strokeWidth={1.5} />
            <Zap className="w-3 h-3 text-[#c15337] absolute -bottom-0.5 -right-0.5" fill="#c15337" strokeWidth={0} />
          </div>
          <span className="font-heading text-xl font-bold tracking-tight text-[#114232]">Nyoksure</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher compact />
          <a href="#tiers" className="hidden sm:inline text-sm text-[#5c6360] hover:text-[#114232] transition-colors">Tiers</a>
          <a href="#how" className="hidden sm:inline text-sm text-[#5c6360] hover:text-[#114232] transition-colors">How it works</a>
          <Link to="/docs/embed" data-testid="landing-nav-developers" className="hidden sm:inline text-sm text-[#5c6360] hover:text-[#114232] transition-colors">Developers</Link>
          {dashHref ? (
            <Link
              to={dashHref}
              data-testid="landing-continue-to-dashboard"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#c15337] text-white text-sm font-semibold hover:bg-[var(--terracotta-hover,#a8462e)] transition-colors"
            >
              <LayoutDashboard className="w-3.5 h-3.5" />
              Continue to dashboard
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          ) : (
            <Link
              to="/signup"
              data-testid="landing-cta-login"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#114232] text-[#f4f2eb] text-sm font-semibold hover:bg-[#0d3125] transition-colors"
            >
              Sign up <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          )}
        </div>
      </nav>

      {/* Hero */}
      <header className="px-6 lg:px-10 max-w-7xl mx-auto pt-12 lg:pt-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 space-y-7">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#c15337]/10 text-[#c15337] text-[11px] font-bold uppercase tracking-[0.18em]">
            <Zap className="w-3 h-3 fill-[#c15337]" /> FlashClaim — paid in seconds
          </div>
          <h1 className="font-heading text-5xl lg:text-7xl font-extrabold leading-[0.95] text-[#114232]">
            Cyber cover for every <span className="text-[#c15337]">Kenyan</span> merchant.
          </h1>
          <p className="text-lg text-[#5c6360] max-w-xl leading-relaxed">
            Embedded micro-insurance against M-Pesa fraud, phishing, account takeover,
            and POS settlement loss. From <span className="font-mono font-semibold text-[#114232]">KES 99 / week</span>.
            Billed weekly, paid in seconds.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="#tiers"
              data-testid="landing-cta-explore"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#114232] text-[#f4f2eb] font-semibold hover:bg-[#0d3125] transition-colors"
            >
              Explore tiers <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://wa.me/254700000000?text=Niko%20tayari%20kujisajili%20Nyoksure"
              target="_blank" rel="noreferrer"
              data-testid="landing-cta-whatsapp"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#114232]/20 bg-white hover:bg-[#114232]/5 text-[#114232] font-semibold transition-colors"
            >
              <MessageCircle className="w-4 h-4 text-[#25D366]" /> Sign up via WhatsApp
            </a>
          </div>
          <div className="flex flex-wrap gap-5 pt-3 text-sm text-[#5c6360]">
            <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-[#114232]" /> IRA-aligned</span>
            <span className="inline-flex items-center gap-1.5"><Check className="w-4 h-4 text-[#114232]" /> 7-day cooling-off</span>
            <span className="inline-flex items-center gap-1.5"><Smartphone className="w-4 h-4 text-[#114232]" /> WhatsApp-first</span>
          </div>
        </div>

        {/* Hero card */}
        <div className="lg:col-span-5">
          <div
            className="relative rounded-2xl overflow-hidden border border-[#114232]/10 bg-white shadow-[0_24px_60px_rgba(17,66,50,0.12)]"
            data-testid="landing-hero-card"
          >
            <div
              className="h-56 lg:h-72 bg-cover bg-center"
              style={{ backgroundImage: "url('https://images.unsplash.com/photo-1761370980657-22586ea44093?crop=entropy&cs=srgb&fm=jpg&q=80&w=1200')" }}
            />
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#c15337] font-bold">Live claim</div>
                <span className="badge-status badge-flash">FlashClaim 41s</span>
              </div>
              <div>
                <div className="text-[10px] text-[#5c6360] uppercase tracking-wider font-semibold">Payout</div>
                <div className="font-mono text-3xl font-bold text-[#114232]">KES 8,500</div>
              </div>
              <div className="text-sm text-[#5c6360]">
                <span className="text-[#1b1d1c] font-medium">Mama Wanjiru</span> filed an
                M-Pesa fraud claim from Nairobi — paid before her chai went cold.
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tiers */}
      <section id="tiers" className="px-6 lg:px-10 max-w-7xl mx-auto pt-24 pb-12">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337] mb-2">Choose your tier</div>
        <h2 className="font-heading text-4xl font-bold text-[#114232] mb-12">Cover starts at KES 99 / week.</h2>
        <div className="grid md:grid-cols-3 gap-6">
          {TIERS.map((t) => (
            <div
              key={t.code}
              data-testid={`tier-card-${t.code}`}
              className={`relative bg-white rounded-2xl p-7 border border-[#114232]/10 flex flex-col gap-5 transition-transform hover:-translate-y-1 ${
                t.flash ? "ring-1 ring-[#c15337]/30" : ""
              }`}
            >
              {t.flash && (
                <span className="absolute top-0 left-6 right-6 h-[3px] bg-[#c15337] rounded-b" />
              )}
              <div>
                <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#5c6360]">{t.flash ? "Best for SMEs" : "Tier"}</div>
                <div className="font-heading text-2xl font-bold text-[#114232] mt-1">{t.name}</div>
              </div>
              <div className="flex items-baseline gap-1">
                <span className="font-mono text-4xl font-bold text-[#114232]">KES {t.premium}</span>
                <span className="text-sm text-[#5c6360]">/ week</span>
              </div>
              <div className="text-sm text-[#5c6360]">
                Cover up to <span className="font-mono font-semibold text-[#114232]">KES {t.cover.toLocaleString()}</span>
              </div>
              <ul className="space-y-2 text-sm flex-1">
                {t.perks.map((p) => (
                  <li key={p} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#114232] mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/login"
                data-testid={`tier-cta-${t.code}`}
                className={`mt-2 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-full font-semibold text-sm transition-colors ${
                  t.flash
                    ? "btn-terracotta hover:bg-[var(--terracotta-hover)]"
                    : "btn-baobab hover:bg-[var(--baobab-hover)]"
                }`}
              >
                Get started <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="px-6 lg:px-10 max-w-7xl mx-auto py-16">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#c15337] mb-2">How it works</div>
        <h2 className="font-heading text-4xl font-bold text-[#114232] mb-12">From WhatsApp to payout in 60 seconds.</h2>
        <div className="grid md:grid-cols-4 gap-6">
          {[
            ["01", "Sign up", "Add your phone, business name & tier via WhatsApp or web."],
            ["02", "Pay weekly", "M-Pesa STK push or CapitalPay. Stay covered, week by week."],
            ["03", "File in seconds", "Snap evidence in WhatsApp. Our FlashClaim engine reviews instantly."],
            ["04", "Get paid", "Score < 35 → auto-approve. Mid-risk → human review. Always transparent."],
          ].map(([n, h, b]) => (
            <div key={n} className="bg-white rounded-xl p-6 border border-[#114232]/10">
              <div className="font-mono text-3xl font-bold text-[#c15337]">{n}</div>
              <div className="font-heading text-base font-bold mt-3 text-[#114232]">{h}</div>
              <p className="text-sm text-[#5c6360] mt-2 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Footer CTA */}
      <section className="px-6 lg:px-10 max-w-7xl mx-auto pb-20">
        <div className="bg-[#114232] rounded-2xl p-10 lg:p-14 text-[#f4f2eb] flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#d49a36] mb-2">Ready when you are</div>
            <h3 className="font-heading text-3xl font-bold leading-tight">Start covering your business today.</h3>
            <p className="text-[#f4f2eb]/80 mt-2 max-w-lg">No paperwork. No agents. Just KES 99 a week — paid via M-Pesa.</p>
          </div>
          <Link
            to="/signup"
            data-testid="landing-footer-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#c15337] hover:bg-[#a8472f] text-white font-semibold transition-colors"
          >
            Get covered <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
